import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Future 2030 Retail outlook page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **The fourth Future 2030 outlook with a designed page of its own**, and the
 * thirteenth report overall. Asked for on 2026-08-28, in the same words as the
 * Automotive, Manufacturing and Consumer Goods ones — *"referans alarak ilgili
 * sayfayı birebir tıpa tıp aynısını tasarla"* — as a four-part full-page capture
 * of digitopia.co's Retail outlook page, plus the six images that page carries:
 * the shopping-bag cover art, the DMI forecast chart, the three requirement
 * photographs and the two-copy report mockup. And, as with the Consumer Goods
 * request, the instruction that the maturity band point at the 2026 DAIMI report
 * rather than the 2024 one the live page offers.
 *
 * So the bands are the reference's, in the reference's order: hero → the outlook
 * beside Figure 1 → a dark band of three imperatives → the five predictions with
 * the mockup → the pointer at the maturity report → the close. The two shared
 * closing bands (`content/reportClosing.ts`) still sit where they sit on every
 * other report page.
 *
 * **The shape is the Manufacturing page's, not the Automotive one's**: the outlook
 * band wraps its three trajectories in a lead-in and a closing line, and the
 * predictions are five plain statements under a lead-in rather than
 * label-and-figure pairs. Two things are this page's own. Its dark band's second
 * line — "3 Imperatives for Success" — is a title rather than a second claim, the
 * way Consumer Goods' is. And its predictions band opens with two paragraphs
 * rather than one, because the live band sets the sector's state of play above the
 * line that introduces the list.
 *
 * **Two deliberate departures from the capture**, both of them the settled house
 * call rather than a reading of the reference:
 *
 *  * the h1 is the series headline every outlook page here uses ("Digital
 *    Transformation Outlook for Retail") rather than the live page's "Future 2030:
 *    Retail Industry Outlook" — the eyebrow above it already says Future 2030, and
 *    the four outlook pages read as one series this way.
 *  * the maturity band carries the 2026 Retail Industry Focus cover, which the
 *    live band does not. `OUTLOOK_RT_MATURITY.check` is where that is asked about,
 *    because it is the one place this page shows something the reference has not.
 *
 * **Where the words come from.** Everything here is transcribed from the four
 * captures, which are full-page renders read down to fit — the *shape* of the page
 * is certain and the *wording* is a careful reading of body copy set a handful of
 * pixels tall. Every band that carries prose therefore carries a `check`, the same
 * way `content/outlookManufacturing.ts` does, and `outlookRetailChecks()` feeds
 * them into the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form. These documents are prerendered with
 * no client bundle at all (`src/prerender.tsx`), so a form script would never run —
 * and the other thirty reports on this site hand the reader the PDF from our own
 * bucket instead. Every Download button here is that PDF, and
 * `OUTLOOK_RT_CLOSE.check` is what keeps the gating question open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The short slug digitopia.co uses, and the one the request named. It was already
 * in `REPORTS` before this page existed, so the page is built under it as it
 * stands rather than moved to a `…-outlook-2030` one.
 */
export const OUTLOOK_RT_SLUG = 'retail-industry-outlook'

/**
 * The Retail Industry Focus cut of the Digital & AI Maturity Report 2026 — where
 * this page's maturity band sends the reader.
 *
 * Written out rather than imported: that report is one of the twenty drawn by the
 * shared template in `pages/Reports.tsx`, so it has no content module of its own
 * to export a slug from. It is the literal passed to `focus()` in
 * `content/reports.ts`, and the two have to stay in step.
 */
export const DAIMI_RT_SLUG = 'digital-and-ai-maturity-report-2026-retail-industry'

/**
 * The hero.
 *
 * `lede` is set bold above the paragraph because that is how the live hero sets
 * it — one question in bold, the reason for it underneath — and the button says
 * "Download the Report" rather than the "Download Report" every other band uses,
 * which is also the live page's wording.
 */
export const OUTLOOK_RT_INTRO = {
  lede: 'Will your brand stay ahead by 2030?',
  body: 'Retail is being redefined by hyper-personalization, AI, and real-time customer engagement. Discover where the industry is heading, and how to lead the way.',
  downloadLabel: 'Download the Report',
  /**
   * The cover art supplied with this request — the shopping bag on a circuit
   * board, which is the artwork behind the report cover with none of its lettering
   * on it. This is where the live hero puts it too, at the same size and on the
   * same side.
   */
  photo: {
    src: '/photo/future-2030-retail-digital-transformation.webp',
    alt: 'A shopping bag rendered in brushed metal, standing on a glowing circuit board',
  } satisfies Photo,
  check:
    'The hero’s two lines are transcribed from the reference capture rather than supplied as text. Read them back against digitopia.co before production. The image, its side and its size are the live page’s.',
}

/**
 * The outlook band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one block on the live page in which each line opens with a bolded
 * label — "Leaders", "Average", "Laggards". Split into `label` and `body` here so
 * the label can be set rather than emphasised inside a sentence, which is the call
 * every designed report page on this site has made.
 *
 * `intro` and `outro` are the lines the live band wraps them in; the closing one
 * is two sentences here rather than the Manufacturing band's one.
 */
export const OUTLOOK_RT_FIGURE = {
  heading: 'A Growing Digital Divide in Retail',
  intro: 'Digitopia’s forecast outlines what the next five years hold:',
  points: [
    {
      label: 'Leaders',
      body: 'could reach 4.33 maturity by 2029, shaping intelligent, predictive retail ecosystems.',
    },
    {
      label: 'Average',
      body: 'retailers face a tipping point: accelerate now or plateau at 2.90.',
    },
    {
      label: 'Laggards',
      body: 'must reinvent or risk irrelevance.',
    },
  ],
  outro:
    'How prepared is your organization for the future of commerce? Your brand’s future is being decided today.',
  /**
   * Figure 1, the file supplied with this request: five years of actuals (2020–24)
   * and five of forecast (2025–29), the leader, average and laggard trajectories
   * each fanning out into an optimistic, moderate and pessimistic band. It is
   * 1200×784 on a white ground, so it gets a white card of its own and scales to it
   * whole — ten year columns with three labelled values each do get small on a
   * phone, but a figure the reader has to drag sideways reads as broken.
   */
  photo: {
    src: '/photo/future-2030-retail-dmi-forecast.webp',
    alt: 'Retail DMI evolution 2020 to 2029: actual leader, average and laggard scores to 2024, then optimistic, moderate and pessimistic forecast bands to 2029',
  } satisfies Photo,
  caption: [
    'Figure 1: Retail Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'Figure 1 is the file you supplied and is shown whole. Two readings wanted on the words beside it. This band turns on two figures — leaders at “4.33 maturity by 2029” and average retailers plateauing at “2.90” — and both agree with the chart’s own 2029 labels (4.33 optimistic for leaders, 2.90 pessimistic for average), so the page and the figure are consistent; the report still has to stand behind them. And the live page calls the index DMI here while every other page on this site calls it DAIMI, the Digital & AI Maturity Index; the chart itself is lettered DMI, so both were left as they are. Say which name this page should use.',
}

/**
 * The dark imperatives band: two headline lines, then three cards.
 *
 * The live band's second line is not a sentence but a title — "3 Imperatives for
 * Success" — so it is set lighter and larger under the headline rather than as a
 * second claim, which is what the shared component already does with it.
 */
export const OUTLOOK_RT_REQUIREMENTS = {
  heading: 'Retail’s Digital Revolution Has Arrived',
  subheading: '3 Imperatives for Success',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'Digital Operations',
      body: 'Optimize your value chain with automation, analytics, and real-time visibility.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
      photo: {
        src: '/photo/retail-digital-operations.webp',
        alt: 'A shopper standing in a shopping mall lit by cyan display screens',
      } satisfies Photo,
    },
    {
      title: 'Customer First',
      body: 'Use data and AI to deliver personalized, seamless, omnichannel experiences.',
      linkLabel: 'Artificial Intelligence',
      linkHref: LINKS.enterpriseAi,
      photo: {
        src: '/photo/retail-customer-first.webp',
        alt: 'A shopper in a VR headset reaching for products on a holographic store display',
      } satisfies Photo,
    },
    {
      title: 'Empowered Teams',
      body: 'Build digital skills and a culture that embraces change and innovation.',
      linkLabel: 'Digital Upskilling',
      linkHref: LINKS.transformation,
      photo: {
        src: '/photo/retail-empowered-teams.webp',
        alt: 'Four colleagues around a meeting table reading sales dashboards on a laptop',
      } satisfies Photo,
    },
  ],
  check:
    'Two things. The two headline lines and the three card texts are transcribed from the reference capture. And the third card’s link is the one destination that had to change: the live page sends “Digital Upskilling” to a solution page digitopia.co has and this site does not — it came across in the import as a rebuild source rather than as a page, so linking to it would be a dead link. It points at Transformation Management until that page is built. The other two are the live destinations. This is the same substitution the Automotive, Manufacturing and Consumer Goods outlook pages make, and one answer settles all four.',
}

/**
 * The predictions band, with the report mockup beside it.
 *
 * Five plain statements rather than the Automotive page's label-and-figure pairs,
 * because that is what the live band prints. Two lead-in paragraphs rather than
 * one: the live band states where the sector is before it introduces the list.
 */
export const OUTLOOK_RT_PREDICTIONS = {
  heading: 'Five Predictions for Retail 2030',
  intro: [
    'From instant gratification to immersive commerce, retail is evolving fast. The most adaptive brands are already building AI-first operations, optimizing every touchpoint, and personalizing every journey.',
    'The report explores five megatrends shaping the decade:',
  ],
  items: [
    'Omnichannel integration becomes the standard',
    'Hyper-personalization drives every interaction',
    'Social commerce transforms shopping behaviors',
    'Autonomous retail redefines convenience',
    'Smart supply chains power speed and sustainability',
  ],
  /** The mockup supplied with this request — the Future 2030 cover, not a maturity cut's. */
  photo: {
    src: '/photo/future-2030-outlook-retail-mockup.webp',
    alt: 'Two copies of Future 2030: Digital Transformation Outlook for Retail',
  } satisfies Photo,
  check:
    'All five predictions and both lead-in paragraphs are transcribed from the reference capture. None of the five carries a figure, so what is wanted here is a reading of the wording rather than a check of a number. The mockup beside them is the file you supplied; the reference capture shows that half of the band empty, so say if the live page really carries no image there and it should come out.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's, and the only
 * change in this request that was asked for in words rather than shown in a
 * picture.** digitopia.co sends the reader to a *2024* Retail Digital Maturity
 * Report; the instruction with the captures was *"en aşağıda önceden konuştuğumuz
 * gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. So the CTA, the cover and
 * the sentence all name the Retail Industry Focus cut of the Digital & AI Maturity
 * Report 2026, which is the report this site actually publishes.
 *
 * That is settled, so it is not what `check` asks about: the same swap was left
 * open on the Automotive page and has now been answered four times.
 */
export const OUTLOOK_RT_MATURITY = {
  heading: 'How Does Your Digital Maturity Compare?',
  body: 'The Retail Industry Focus cut of the Digital & AI Maturity Report 2026 reveals where companies stand today, and where they need to go. Compare your performance with leaders, average performers, and laggards. Digitopia’s DMI data shows a growing gap between those moving fast and those falling behind.',
  ctaLabel: 'Explore the Retail Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_RT_SLUG}`,
  /** That report's cover, which this site already holds. */
  photo: {
    src: '/photo/daimi-report-2026-retail-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Retail Focus',
  } satisfies Photo,
  check:
    'The destination here is decided, not transcribed: this band points at the 2026 report rather than the “2024 Retail Digital Maturity Report” the live page offers, which is what you asked for — so the CTA label and the sentence naming the report are the 2026 cut’s. Two things to decide. The rest of the paragraph is the live one word for word, so it wants a reading. And the cover beside it is not on the live page at all: it is here because the other three Future 2030 outlook pages show the maturity cut they point at, and it should come out if this band is meant to stay text-only.',
}

/** The closing band, which is where the live page's gated form sits. */
export const OUTLOOK_RT_CLOSE = {
  heading: 'Lead the Change',
  body: 'Learn how top retailers are preparing for 2030, and how you can join them.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `outlookManufacturingChecks()` uses, and appended
 * to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file. This page
 * arrived with all six of its images. What is left is transcription nobody has
 * read against the original, one destination this site cannot offer, one image
 * this page shows that the reference does not, and the gating decision.
 */
export function outlookRetailChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_RT_INTRO.check },
    { section: 'outlook', check: OUTLOOK_RT_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_RT_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_RT_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_RT_MATURITY.check },
    { section: 'download', check: OUTLOOK_RT_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_RT_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
